import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";

import { getState } from "../../../../../state/store";
import { selectDeviceByDusn } from "../../../../../state/selectors";

import {
  alignItems,
  deviceOptions,
  deviceTypeOptions as allDeviceTypeOptions,
  strCode
} from "../../../../../consts";
import { notificationService, t } from "../../../../../services";
import {
  dateUtil,
  deviceUtil,
  dropdownUtil,
  fleetManagementUtil,
  groupUtils,
  objectUtil
} from "../../../../../utils";
import { groupUtils as deviceGroupUtils } from "../../utils/groupUtils";

import {
  DropdownInput,
  FlexContainers,
  Input,
  WrapperContainer,
  DateInput,
  TimeInput,
  SVG,
  IconButton
} from "../../../../../components";

import {
  SectionTitle,
  InputSectionWrapper,
  InfoText,
  HardResetButton,
  FormWrapperContainer
} from "./styled";
import FirmwareVersionInput from "../../../../../components/FirmwareVersionInput/FirmwareVersionInput";

const firmwareInputConfig = [
  {
    id: "major",
    title: strCode.MAJOR_TITLE,
    min: 0,
    max: 99
  },
  {
    id: "minor",
    title: strCode.MINOR_TITLE,
    min: 0,
    max: 99
  },
  {
    id: "patch",
    title: strCode.PATCH_TITLE,
    min: 0,
    max: 99
  }
];

const defaultVersionValues = {
  major: null,
  minor: null,
  patch: null
};

export const NewDeviceForm = forwardRef(
  (
    {
      deviceData,
      originalData,
      groupOptions = [],
      isEdit,
      loading,
      onSaveDevice,
      setIsSameData,
      setIsMercuryDeviceSelected,
      onResetDevice,
      onConfigureGroups
    },
    ref
  ) => {
    const [dusn, setDusn] = useState("");
    const [codentify, setCodentify] = useState("");

    const [relatedDusn, setRelatedDusn] = useState("");
    const [kitCodentify, setKitCodentify] = useState("");

    const [selectedGroups, setSelectedGroups] = useState([]);

    const [selectedModel, setSelectedModel] = useState();
    const [selectedType, setSelectedType] = useState();

    const [materialBrandFamily, setMaterialBrandFamily] = useState("");
    const [materialColor, setMaterialColor] = useState("");
    const [materialGroup, setMaterialGroup] = useState("");
    const [materialModelNumber, setMaterialModelNumber] = useState("");

    const [latestFwVersion, setLatestFwVersion] = useState(defaultVersionValues);
    const [updateDate, setUpdateDate] = useState();
    const [updateTime, setUpdateTime] = useState();

    const [isValid, setIsValid] = useState(false);
    const [isModelValid, setIsModelValid] = useState(false);
    const [isTypeValid, setIsTypeValid] = useState(false);

    const [showErrors, setShowErrors] = useState(false);
    const [missingFields, setMissingFields] = useState({});

    const [deviceTypeOptions, setDeviceTypeOptions] = useState(allDeviceTypeOptions);

    const formRef = useRef(null);

    const isMercuryDevice = useMemo(() => {
      if (deviceData && deviceData?.selectedModel) {
        return deviceUtil.getIsMercuryDevice(deviceData.selectedModel.id);
      }

      return false;
    }, [deviceData]);

    const getNewDeviceData = useCallback(() => {
      let newData = {
        dusn,
        codentify,
        relatedDusn,
        kitCodentify,
        selectedGroups,
        selectedModel,
        selectedType,
        materialBrandFamily,
        materialColor,
        materialGroup,
        materialModelNumber,
        updateDate,
        updateTime,
        latestFwVersion
      };

      return newData;
    }, [
      dusn,
      codentify,
      relatedDusn,
      kitCodentify,
      selectedGroups,
      selectedModel,
      selectedType,
      materialBrandFamily,
      materialColor,
      materialGroup,
      materialModelNumber,
      updateDate,
      updateTime,
      latestFwVersion
    ]);

    useEffect(() => {
      prepopulateData();

      return () => {
        resetFormData();
      };
    }, [deviceData]);

    useEffect(() => {
      if (selectedModel) {
        const filteredDeviceTypeOptions = fleetManagementUtil.filterDeviceTypeOptions(
          selectedModel.id
        );

        setDeviceTypeOptions(filteredDeviceTypeOptions);

        if (
          selectedType &&
          !filteredDeviceTypeOptions.map((opt) => opt.id).includes(selectedType.id)
        ) {
          setSelectedType(filteredDeviceTypeOptions[0]);
        }

        setIsMercuryDeviceSelected(deviceUtil.getIsMercuryDevice(selectedModel.id));
      } else {
        setDeviceTypeOptions(allDeviceTypeOptions);
        setIsMercuryDeviceSelected(false);
      }
    }, [selectedModel]);

    useEffect(() => {
      let valid = dusn?.length === 22 && codentify?.length === 14 && selectedModel && selectedType;

      const versionValues = Object.values(latestFwVersion);
      const emptyVersionValuesLength = versionValues.filter((v) => v == null)?.length;

      const isFwVersionValid =
        [0, 3].includes(emptyVersionValuesLength) || emptyVersionValuesLength === 1;

      let isModelValid = true;
      let isTypeValid = true;

      const isKitCodentityValid = kitCodentify?.length
        ? kitCodentify?.length === 14
        : !relatedDusn?.length;

      const relatedCodentify = selectDeviceByDusn(relatedDusn)(getState())?.codentify ?? null;
      const isRelatedDusnValid = relatedDusn?.length
        ? relatedDusn?.length === 22 && !!relatedCodentify
        : true;

      if (dusn?.length === 22 && selectedModel && selectedType) {
        const { isDeviceModelValid, isDeviceTypeValid } = fleetManagementUtil.validateDusn(
          dusn,
          selectedModel.id,
          selectedType.id
        );

        isModelValid = isDeviceModelValid;
        isTypeValid = isDeviceTypeValid;
      }

      valid =
        valid &&
        isFwVersionValid &&
        isModelValid &&
        isTypeValid &&
        isKitCodentityValid &&
        isRelatedDusnValid;

      const errorFields = {
        dusn: dusn?.length !== 22 || !isModelValid || !isTypeValid,
        codentify: codentify?.length !== 14,
        relatedDusn: !isRelatedDusnValid,
        kitCodentify: !isKitCodentityValid,
        selectedModel: !selectedModel || !isModelValid,
        selectedType: !selectedType || !isTypeValid,
        latestFwVersion: !isFwVersionValid
      };

      setMissingFields(errorFields);

      setIsValid(valid);
      setIsModelValid(isModelValid);
      setIsTypeValid(isTypeValid);
    }, [dusn, codentify, relatedDusn, kitCodentify, selectedModel, selectedType, latestFwVersion]);

    useEffect(() => {
      if (deviceData && originalData) {
        const newData = getNewDeviceData();

        if (objectUtil.isNullOrDefault(newData.latestFwVersion)) {
          newData.latestFwVersion = null;
        }

        setIsSameData(objectUtil.objEquals(originalData, newData));
      }
    }, [
      dusn,
      codentify,
      relatedDusn,
      kitCodentify,
      selectedGroups,
      selectedModel,
      selectedType,
      materialBrandFamily,
      materialColor,
      materialGroup,
      materialModelNumber,
      updateDate,
      updateTime,
      latestFwVersion
    ]);

    useEffect(() => {
      if (selectedGroups?.length) {
        const updatedGroups = deviceGroupUtils.updateSelectedGroupOptions(
          groupOptions,
          selectedGroups
        );
        setSelectedGroups(updatedGroups);
      }
    }, [groupOptions, selectedGroups?.length]);

    const prepopulateData = useCallback(() => {
      if (deviceData) {
        setDusn(deviceData?.dusn);
        setCodentify(deviceData?.codentify);

        setRelatedDusn(deviceData?.relatedDusn);
        setKitCodentify(deviceData?.kitCodentify);

        setSelectedGroups(deviceData?.selectedGroups ?? []);

        setSelectedModel(deviceData?.selectedModel);
        setSelectedType(deviceData?.selectedType);

        setMaterialBrandFamily(deviceData?.materialBrandFamily);
        setMaterialColor(deviceData?.materialColor);
        setMaterialGroup(deviceData?.materialGroup);
        setMaterialModelNumber(deviceData?.materialModelNumber);

        setLatestFwVersion(deviceData?.latestFwVersion ?? defaultVersionValues);
        setUpdateDate(deviceData?.updateDate);
        setUpdateTime(deviceData?.updateTime);
      }
    }, [deviceData]);

    const resetFormData = () => {
      setDusn("");
      setCodentify("");

      setRelatedDusn("");
      setKitCodentify("");

      setSelectedGroups([]);

      setSelectedModel(null);
      setSelectedType(null);

      setMaterialBrandFamily("");
      setMaterialColor("");
      setMaterialGroup("");
      setMaterialModelNumber("");

      setLatestFwVersion(defaultVersionValues);
      setUpdateDate(null);
      setUpdateTime(null);

      setIsValid(true);
      setIsSameData(false);
      setShowErrors(false);
      setMissingFields({});

      formRef.current.scrollTo(0, 0);
    };

    const onSubmit = () => {
      if (!showErrors) {
        setShowErrors(true);
      }

      if (!isValid) {
        if (!isModelValid || !isTypeValid) {
          notificationService.showError({
            title: t(strCode.DUSN_VALIDATION_ERROR_TITLE),
            message: t(
              !isModelValid
                ? strCode.DUSN_VALIDATION_MODEL_ERROR_INFO
                : strCode.DUSN_VALIDATION_TYPE_ERROR_INFO
            )
          });
        } else {
          notificationService.showError({
            title: t(strCode.MISSING_FIELDS),
            message: t(strCode.MISSING_FIELDS_MESSAGE)
          });
        }

        return;
      }

      const data = getNewDeviceData();

      onSaveDevice(data);

      return data;
    };

    useImperativeHandle(ref, () => ({
      resetFormData() {
        resetFormData();
      },
      submit() {
        onSubmit();
      },
      getDeviceData() {
        return getNewDeviceData();
      }
    }));

    return (
      <>
        <FormWrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          gap={16}
          width={"100%"}
          innerRef={formRef}>
          <WrapperContainer mb={-8}>
            <InfoText>
              <span>*</span> {t(strCode.REQUIERED_FIELD_INFO)}
            </InfoText>
          </WrapperContainer>
          <SectionTitle>{t(strCode.DEVICE_INFO_TITLE)}</SectionTitle>

          <InputSectionWrapper>
            <Input
              id={"dusn"}
              title={t(strCode.DUSN_TITLE)}
              placeholder={t(strCode.DUSN_PLACEHOLDER)}
              value={dusn}
              setValue={setDusn}
              maxLength={22}
              hasError={showErrors && missingFields?.dusn}
              disabled={isEdit || loading}
              isRequired
            />

            <Input
              id={"codentify"}
              title={t(strCode.CODENTIFY_TITLE)}
              placeholder={t(strCode.CODENTIFY_TITLE)}
              value={codentify}
              setValue={setCodentify}
              maxLength={14}
              hasError={showErrors && missingFields?.codentify}
              disabled={isEdit || loading}
              isRequired
            />
          </InputSectionWrapper>

          <InputSectionWrapper>
            <DropdownInput
              id="group-selector"
              title={t(strCode.USER_GROUP_TITLE)}
              placeholder={t(strCode.USER_GROUP_TITLE)}
              options={groupOptions}
              selectedOptions={selectedGroups}
              onSelectOption={(id) => {
                setSelectedGroups(
                  dropdownUtil.updateSelectedOptions(id, selectedGroups, groupOptions, true, true)
                );
              }}
              onOptionsFiltered={(filteredOptions, searchText) =>
                groupUtils.getFilteredOptions(filteredOptions, searchText)
              }
              InputComponent={
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfigureGroups();
                  }}
                  tooltipLabel={t(strCode.GROUP_SETTINGS_TOOLTIP)}>
                  <SVG.GearIcon />
                </IconButton>
              }
              isSearchable
              useChips
              isRequired
            />
          </InputSectionWrapper>

          <InputSectionWrapper>
            <DropdownInput
              id="model-selector"
              title={t(strCode.MODEL_TITLE)}
              placeholder={t(strCode.SELECT_PLACEHOLDER)}
              options={deviceOptions}
              selectedOption={selectedModel}
              onSelectOption={(id) => {
                setSelectedModel(deviceOptions.find((opt) => opt.id === id));
              }}
              hasError={showErrors && missingFields?.selectedModel}
              disabled={loading}
              isRequired
            />

            <DropdownInput
              id="type-selector"
              title={t(strCode.TYPE_TITLE)}
              placeholder={t(strCode.SELECT_PLACEHOLDER)}
              options={deviceTypeOptions}
              selectedOption={selectedType}
              onSelectOption={(id) => {
                setSelectedType(deviceTypeOptions.find((opt) => opt.id === id));
              }}
              hasError={showErrors && missingFields?.selectedType}
              disabled={loading}
              isRequired
            />
          </InputSectionWrapper>

          <InputSectionWrapper>
            <Input
              id={"related-dusn"}
              title={t(strCode.RELATED_DUSN_TITLE)}
              placeholder={t(strCode.DUSN_PLACEHOLDER)}
              value={relatedDusn}
              setValue={setRelatedDusn}
              maxLength={22}
              hasError={showErrors && missingFields?.relatedDusn}
              disabled={(isEdit && originalData?.relatedDusn) || loading}
            />

            <Input
              id={"kit-codentify"}
              title={t(strCode.KIT_CODENTIFY_TITLE)}
              placeholder={t(strCode.KIT_CODENTIFY_TITLE)}
              value={kitCodentify}
              setValue={setKitCodentify}
              maxLength={14}
              hasError={showErrors && missingFields?.kitCodentify}
              disabled={(isEdit && originalData?.kitCodentify) || loading}
            />
          </InputSectionWrapper>

          <InputSectionWrapper>
            <Input
              id={"material-brand-family"}
              title={t(strCode.MATERIAL_BRAND_TITLE)}
              placeholder={t(strCode.MATERIAL_BRAND_TITLE)}
              value={materialBrandFamily}
              setValue={setMaterialBrandFamily}
              hasError={showErrors && missingFields?.materialBrandFamily}
              disabled={loading}
            />

            <Input
              id={"material-color"}
              title={t(strCode.MATERIAL_COLOR_TITLE)}
              placeholder={t(strCode.MATERIAL_COLOR_TITLE)}
              value={materialColor}
              setValue={setMaterialColor}
              hasError={showErrors && missingFields?.materialColor}
              disabled={loading}
            />
          </InputSectionWrapper>

          <InputSectionWrapper>
            <Input
              id={"material-group"}
              title={t(strCode.MATERIAL_GROUP_TITLE)}
              placeholder={t(strCode.MATERIAL_GROUP_TITLE)}
              value={materialGroup}
              setValue={setMaterialGroup}
              hasError={showErrors && missingFields?.materialGroup}
              disabled={loading}
            />

            <Input
              id={"material-model-number"}
              title={t(strCode.MATERIAL_MODEL_NUMBER_TITLE)}
              placeholder={t(strCode.MATERIAL_MODEL_NUMBER_TITLE)}
              value={materialModelNumber}
              setValue={setMaterialModelNumber}
              hasError={showErrors && missingFields?.materialModelNumber}
              disabled={loading}
            />
          </InputSectionWrapper>

          <SectionTitle>{t(strCode.LATEST_FW_VERSION_TITLE)}</SectionTitle>

          <InputSectionWrapper>
            <FirmwareVersionInput
              firmwareInputConfig={firmwareInputConfig}
              values={latestFwVersion}
              setValues={setLatestFwVersion}
              hasError={showErrors && missingFields?.latestFwVersion}
              disabled={loading}
            />

            {isEdit && updateDate && updateTime && (
              <>
                <DateInput
                  id={"update-date"}
                  title={t(strCode.DATE_OF_UPDATE_TITLE)}
                  value={updateDate}
                  setValue={setUpdateDate}
                  minDate={dateUtil.getUTCDate()}
                  disabled
                  noTime
                />

                <TimeInput
                  id={"update-time"}
                  title={t(strCode.TIME_OF_UPDATE_TITLE)}
                  value={updateTime}
                  setValue={setUpdateTime}
                  minDate={dateUtil.getUTCDate()}
                  disabled
                />
              </>
            )}
          </InputSectionWrapper>

          {isEdit && isMercuryDevice && (
            <HardResetButton onClick={onResetDevice} disabled={loading}>
              <WrapperContainer Container={FlexContainers.FlexRow} gap={8}>
                <SVG.HardResetIcon />
                <span>{t(strCode.HARD_RESET_BTN)}</span>
              </WrapperContainer>
            </HardResetButton>
          )}
        </FormWrapperContainer>
      </>
    );
  }
);

NewDeviceForm.displayName = "NewDeviceForm";

NewDeviceForm.propTypes = {
  deviceData: PropTypes.shape({
    dusn: PropTypes.string.isRequired,
    codentify: PropTypes.string.isRequired,
    selectedGroups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ),
    selectedModel: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }),
    selectedType: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }),

    relatedDusn: PropTypes.string,
    kitCodentify: PropTypes.string,

    materialBrandFamily: PropTypes.string,
    materialColor: PropTypes.string,
    materialGroup: PropTypes.string,
    materialModelNumber: PropTypes.string,

    updateDate: PropTypes.instanceOf(Date),
    updateTime: PropTypes.instanceOf(Date),
    latestFwVersion: PropTypes.object
  }),
  originalData: PropTypes.object,
  groupOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  isEdit: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onSaveDevice: PropTypes.func.isRequired,
  onRemoveDevice: PropTypes.func.isRequired,
  onResetDevice: PropTypes.func.isRequired,
  onConfigureGroups: PropTypes.func.isRequired,
  setIsSameData: PropTypes.func.isRequired,
  setIsMercuryDeviceSelected: PropTypes.func.isRequired
};
